// Simple telemetry tracking for Application Insights
// Works with SWA's built-in Application Insights (no packages needed)

interface TelemetryData {
  name: string;
  properties?: Record<string, any>;
  measurements?: Record<string, number>;
}

// Get user/tenant info from auth context
let currentUserId: string | null = null;
let currentTenantId: string | null = null;
let currentUserRole: string | null = null;

export function setTelemetryUser(userId: string, tenantId: string, role?: string) {
  currentUserId = userId;
  currentTenantId = tenantId;
  currentUserRole = role || null;
  console.log('📊 Telemetry user set:', { userId, tenantId, role });
}

// Track page views
export function trackPageView(pageName: string, properties?: Record<string, any>) {
  const data = {
    name: `PageView_${pageName}`,
    properties: {
      page: pageName,
      userId: currentUserId,
      tenantId: currentTenantId,
      userRole: currentUserRole,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      ...properties
    },
    measurements: {
      loadTime: performance.now()
    }
  };
  
  sendToAppInsights(data);
  console.log('📄 Page view tracked:', pageName);
}

// Track custom events (button clicks, actions, etc.)
export function trackEvent(eventName: string, properties?: Record<string, any>) {
  const data = {
    name: eventName,
    properties: {
      userId: currentUserId,
      tenantId: currentTenantId,
      userRole: currentUserRole,
      timestamp: new Date().toISOString(),
      ...properties
    }
  };
  
  sendToAppInsights(data);
  console.log('🎯 Event tracked:', eventName);
}

// Track performance metrics
export function trackMetric(metricName: string, value: number, properties?: Record<string, any>) {
  const data = {
    name: `Metric_${metricName}`,
    properties: {
      userId: currentUserId,
      tenantId: currentTenantId,
      ...properties
    },
    measurements: {
      [metricName]: value
    }
  };
  
  sendToAppInsights(data);
}

// Track errors
export function trackError(error: Error, properties?: Record<string, any>) {
  const data = {
    name: 'Exception',
    properties: {
      message: error.message,
      stack: error.stack,
      userId: currentUserId,
      tenantId: currentTenantId,
      userRole: currentUserRole,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      ...properties
    }
  };
  
  sendToAppInsights(data);
  console.error('❌ Error tracked:', error.message);
}

// Send data to Application Insights via SWA's built-in endpoint
function sendToAppInsights(data: TelemetryData) {
  // For development, just log
  if (import.meta.env.DEV) {
    console.log('📊 [DEV] Telemetry:', data);
    return;
  }
  
  // In production, just log to console for now
  // TODO: Set up proper Application Insights endpoint
  console.log('📊 [PROD] Telemetry:', data);
  
  // Disabled until /api/telemetry endpoint is implemented
  /*
  // In production, SWA automatically tracks via its middleware
  // We can also use the browser's beacon API for reliable delivery
  try {
    // Use custom dimension logging that SWA will pick up
    const logData = {
      type: 'customEvent',
      timestamp: new Date().toISOString(),
      ...data
    };
    
    // Send as a custom log entry (SWA will forward to App Insights)
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(logData)], { type: 'application/json' });
      navigator.sendBeacon('/api/telemetry', blob);
    }
  } catch (error) {
    console.error('Failed to send telemetry:', error);
  }
  */
}

// Track page load performance
export function trackPageLoadMetrics() {
  if (typeof window === 'undefined' || !window.performance) return;
  
  window.addEventListener('load', () => {
    setTimeout(() => {
      const perfData = window.performance.timing;
      const loadTime = perfData.loadEventEnd - perfData.navigationStart;
      const domReadyTime = perfData.domContentLoadedEventEnd - perfData.navigationStart;
      const renderTime = perfData.domComplete - perfData.domLoading;
      
      trackMetric('PageLoadTime', loadTime, {
        page: window.location.pathname,
        domReadyTime,
        renderTime
      });
    }, 0);
  });
}

// Initialize telemetry
export function initTelemetry() {
  console.log('📊 Telemetry initialized');
  trackPageLoadMetrics();
}
